import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Picker from 'react-mobile-picker';
import { format } from 'date-fns';
import { ROUTES } from '@app/router/routes';
import { usePageLayout } from '@app/router';
import { randomLampImage, DEFAULT_LAMPS, getLampImageByTitle } from '../constants';
import { getVowRecordList, getVowRecordDescribe } from '../api/lampService';
import './LampBulletinPage.css';
import { useLanguage } from '@shared/contexts/LanguageContext';

const TITLE_ZH_MAP = {
    '身體健康': '身體健康',
    '家庭和樂': '家庭和樂',
    '道業成就': '道業成就',
    '學業進步': '學業進步',
    '事業順利': '事業順利',
    '廣結善緣': '廣結善緣',
    'Physical health': '身體健康',
    'Family harmony': '家庭和樂',
    'The fulfillment of the path': '道業成就',
    'Steady progress in one\'s studies': '學業進步',
    'Excel in Studies': '學業進步',
    'Success in Career': '事業順利',
    'Develop good affinity with all': '廣結善緣',
};

const ERROR_KEYS = {
    'Insufficient Permission': 'errorNotYours',
    'User does not have permission to access this record': 'errorNotYours',
    'Record not found': 'errorNotFound',
    'Invalid record ID': 'errorInvalidId',
    'Network Error': 'errorNetwork',
    'Request timeout': 'errorTimeout',
};

const START_YEAR = 2020;
const END_YEAR = new Date().getFullYear() + 1;
const yearOptions = Array.from({ length: END_YEAR - START_YEAR + 1 }, (_, idx) => String(START_YEAR + idx));
const monthOptions = Array.from({ length: 12 }, (_, idx) => String(idx + 1).padStart(2, '0'));

const getDayOptions = (year, month) => {
    const daysInMonth = new Date(Number(year), Number(month), 0).getDate();
    return Array.from({ length: daysInMonth }, (_, idx) => String(idx + 1).padStart(2, '0'));
};

const LampBulletinPage = ({
    pageTitle,
    quote,
}) => {
    const navigate = useNavigate();
    const { setLayoutOptions } = usePageLayout();
    const { t, currentLanguage } = useLanguage();
    const resolvedPageTitle = pageTitle ?? { title: t('lamp-bulletin.pageTitle') };
    const resolvedQuote = quote ?? { text: t('lamp-bulletin.quoteText'), dedication: t('lamp-bulletin.quoteDedication') };

    useEffect(() => {
        setLayoutOptions({ noPaddingBottom: true });
        return () => setLayoutOptions({});
    }, [setLayoutOptions]);

    const [lamps, setLamps] = useState(DEFAULT_LAMPS);
    const [foregroundLamps, setForegroundLamps] = useState([]);
    const [backgroundLamps, setBackgroundLamps] = useState([]);
    const [totalQuantity, setTotalQuantity] = useState(null);
    const fgIndexRef = useRef(0);
    const lastFgPositionRef = useRef([]);
    const animPausedRef = useRef(false);
    const isFetchingRef = useRef(false);
    const latestLampsRef = useRef([]);
    const audioRef = useRef(null);
    const [isMuted, setIsMuted] = useState(true);

    // 篩選相關狀態
    const [filterDate, setFilterDate] = useState(null);
    const [filterDonor, setFilterDonor] = useState('');
    const [filterOnlySelf, setFilterOnlySelf] = useState(false);
    const [filteredLamps, setFilteredLamps] = useState(lamps);
    const [filterOpen, setFilterOpen] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [pickerValue, setPickerValue] = useState(() => {
        const now = new Date();
        return {
            year: String(now.getFullYear()),
            month: String(now.getMonth() + 1).padStart(2, '0'),
            day: String(now.getDate()).padStart(2, '0'),
        };
    });

    // 載入 API 資料
    useEffect(() => {
        fetchRecords({});
    }, [currentLanguage]);

    const fetchRecords = (params) => {
        getVowRecordList(params)
            .then((res) => {
                const items = res?.items ?? [];
                setLamps(items);
                setFilteredLamps(items);
                latestLampsRef.current = items;
                resetAnimation();
                if (res?.total_quantity != null) {
                    setTotalQuantity(res.total_quantity);
                }
            })
            .catch((err) => {
                console.error('Failed to load vow records:', err);
            });
    };

    // 重置動畫狀態
    const resetAnimation = () => {
        fgIndexRef.current = 0;
        lastFgPositionRef.current = [];
        setForegroundLamps([]);
    };

    // 離開分頁時暫停新增燈籠，回來時恢復
    useEffect(() => {
        const handleVisibility = () => {
            animPausedRef.current = document.hidden;
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, []);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.muted = true;
        const playPromise = audio.play();
        if (playPromise?.catch) {
            playPromise.catch(() => { });
        }
    }, []);

    const buildParams = ({ date = filterDate, donor = filterDonor, onlySelf = filterOnlySelf } = {}) => {
        const params = {};
        if (date) params.date = format(date, 'yyyy-MM-dd');
        if (donor.trim()) params.vower_name = donor.trim();
        if (onlySelf) params.only_self = true;
        return params;
    };

    const handleDateChange = (date) => {
        setFilterDate(date);
        fetchRecords(buildParams({ date }));
    };

    const openDatePicker = () => {
        const baseDate = filterDate || new Date();
        setPickerValue({
            year: String(baseDate.getFullYear()),
            month: String(baseDate.getMonth() + 1).padStart(2, '0'),
            day: String(baseDate.getDate()).padStart(2, '0'),
        });
        setShowDatePicker(true);
    };

    const handlePickerChange = (nextValue) => {
        const dayOptions = getDayOptions(nextValue.year, nextValue.month);
        const currentDay = dayOptions.includes(nextValue.day)
            ? nextValue.day
            : dayOptions[dayOptions.length - 1];
        setPickerValue({
            ...nextValue,
            day: currentDay,
        });
    };

    const confirmDatePicker = () => {
        const pickedDate = new Date(
            Number(pickerValue.year),
            Number(pickerValue.month) - 1,
            Number(pickerValue.day),
        );
        handleDateChange(pickedDate);
        setShowDatePicker(false);
    };

    const handleDonorChange = (val) => {
        setFilterDonor(val);
        fetchRecords(buildParams({ donor: val }));
    };

    const handleOnlySelfChange = (checked) => {
        setFilterOnlySelf(checked);
        fetchRecords(buildParams({ onlySelf: checked }));
    };

    const handleToggleMute = () => {
        const nextMuted = !isMuted;
        const audio = audioRef.current;
        setIsMuted(nextMuted);
        if (!audio) return;
        audio.muted = nextMuted;
        if (!nextMuted) {
            const playPromise = audio.play();
            if (playPromise?.catch) {
                playPromise.catch(() => {
                    setIsMuted(true);
                    audio.muted = true;
                });
            }
        }
    };

    // 點擊燈籠 - 只有 is_owner 才能看法語開示
    const handleLampClick = async (lamp) => {
        if (!lamp.record_id) {
            console.warn('lamp record_id is missing');
            return;
        }

        try {
            const result = await getVowRecordDescribe(lamp.record_id);
            // 將 API 回傳的資料傳遞到結果頁面
            navigate(ROUTES.BLESSING_RESULT, {
                state: {
                    blessingResult: result,
                }
            });
        } catch (error) {
            console.error('Failed to fetch vow record description:', error);

            // 取得錯誤訊息
            const errorMessage = error?.message || '';
            const errorReason = error?.data?.reason || '';

            const errorKey = ERROR_KEYS[errorMessage] || ERROR_KEYS[errorReason] || 'errorLoadFailed';
            let displayMessage = t(`lamp-bulletin.${errorKey}`);

            // 顯示錯誤訊息
            alert(displayMessage);

            // 特別處理 403 權限不足的錯誤 - 可選擇導向登入頁
            if (error.status === 403) {
                // navigate('/login', { state: { returnTo: '/lamp-bulletin' } });
                return;
            }
        }
    };

    // 前景燈籠 - 隨機間隔（1.5~2.5秒），位置避開上一個
    useEffect(() => {
        if (filteredLamps.length === 0) return;
        latestLampsRef.current = filteredLamps;
        let aborted = false;

        const scheduleNext = () => {
            if (aborted) return;

            if (!animPausedRef.current) {
                const current = latestLampsRef.current;
                const lamp = current[fgIndexRef.current % current.length];

                const allPositions = [25, 50, 75];
                const recentPositions = lastFgPositionRef.current;
                const available = allPositions.filter(p => !recentPositions.includes(p));
                const pool = available.length > 0 ? available : allPositions;
                const picked = pool[Math.floor(Math.random() * pool.length)];
                lastFgPositionRef.current = [...recentPositions, picked].slice(-2);
                
                const title = lamp['title'] || '身體健康'
                // console.log('lamp', lamp);
                // console.log('title', title);
                setForegroundLamps(prev => [...prev, {
                    ...lamp,
                    uniqueId: `fg-${Date.now()}-${Math.random()}`,
                    leftPosition: picked,
                    duration: 18,
                    image: getLampImageByTitle(title),
                }]);
                fgIndexRef.current += 1;

                // 跑完一輪後重新拿 API 資料，用 ref 更新不觸發 useEffect 重跑
                if (fgIndexRef.current % current.length === 0 && !isFetchingRef.current) {
                    isFetchingRef.current = true;
                    getVowRecordList(buildParams())
                        .then((res) => {
                            const items = res?.items ?? [];
                            latestLampsRef.current = items.length > 0 ? items : current;
                            fgIndexRef.current = 0;
                            if (res?.total_quantity != null) {
                                setTotalQuantity(res.total_quantity);
                            }
                        })
                        .catch((err) => {
                            console.error('Failed to reload vow records:', err);
                        })
                        .finally(() => {
                            isFetchingRef.current = false;
                        });
                }
            }

            setTimeout(scheduleNext, 2500 + Math.random() * 1500);
        };

        scheduleNext();
        return () => { aborted = true; };
    }, [filteredLamps]);

    // 背景燈籠 - 兩種移動速度（近層快、遠層慢）
    useEffect(() => {
        const addBackgroundLamp = () => {
            if (animPausedRef.current) return;
            const isFar = Math.random() < 0.5;
            setBackgroundLamps(prev => [...prev, {
                uniqueId: `bg-${Date.now()}-${Math.random()}`,
                leftPosition: 10 + Math.random() * 80,
                duration: isFar ? 18 + Math.random() * 4 : 10 + Math.random() * 3,
                scale: isFar ? 0.35 + Math.random() * 0.15 : 0.55 + Math.random() * 0.2,
                opacity: isFar ? 0.3 : 0.55,
                image: randomLampImage(),
            }]);
        };

        const timer = setInterval(addBackgroundLamp, 500);
        return () => clearInterval(timer);
    }, []);

    // 清理已飄出的燈籠
    useEffect(() => {
        const cleanupTimer = setInterval(() => {
            if (animPausedRef.current) return;
            const now = Date.now();
            const isAlive = (lamp) => {
                const age = (now - parseInt(lamp.uniqueId.split('-')[1])) / 1000;
                return age < lamp.duration + 1;
            };
            setForegroundLamps(prev => prev.filter(isAlive));
            setBackgroundLamps(prev => prev.filter(isAlive));
        }, 2000);

        return () => clearInterval(cleanupTimer);
    }, []);

    return (
        <div className="min-h-screen relative" style={{ background: 'linear-gradient(180deg, var(--color-lamp-bg-end) 0%, var(--color-lamp-bg-mid) 20%, var(--color-lamp-bg-start) 100%) 0 0 / 100% 100vh no-repeat, var(--color-lamp-bg-start)' }}>
            <audio ref={audioRef} src="/music/blessing_music.mp3" loop muted autoPlay playsInline preload="metadata" />

            <button
                type="button"
                onClick={handleToggleMute}
                aria-label={isMuted ? t('lamp-bulletin.unmuteAria') : t('lamp-bulletin.muteAria')}
                className="fixed bottom-20 md:bottom-5 right-3 z-30 flex h-10 w-10 items-center justify-center rounded-full text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)' }}
            >
                <span className="material-symbols-outlined text-xl leading-none">
                    {isMuted ? 'volume_off' : 'volume_up'}
                </span>
            </button>

            {/* Title Section - z-20 全寬背景蓋住燈籠 */}
            <section className="relative pt-12 pb-6 text-center" style={{ zIndex: 20 }}>
                <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-[0.2em]" style={{ color: 'var(--color-lamp-text)' }}>
                    {resolvedPageTitle.title}
                </h1>
                <div className="w-16 md:w-20 h-1 mx-auto mb-3" style={{ background: 'var(--color-lamp-red)' }}></div>

                <div className="max-w-md mx-auto mb-4 px-6">
                    <p className="text-lg md:text-xl leading-relaxed whitespace-pre-line font-medium mb-3" style={{ color: 'var(--color-lamp-text)' }}>
                        {resolvedQuote.text}
                    </p>
                    <p className="text-base font-light italic" style={{ color: 'var(--color-lamp-text)', opacity: 0.7 }}>
                        {resolvedQuote.dedication}
                    </p>
                </div>

                {totalQuantity != null && (
                    <div className="mb-2">
                        <span className="text-base font-medium tracking-wider" style={{ color: 'var(--color-lamp-text)', opacity: 0.85 }}>
                            {t('lamp-bulletin.totalLampsPrefix')}
                        </span>
                        <span className="text-xl font-bold" style={{ color: 'var(--color-lamp-red)' }}>
                            {totalQuantity.toLocaleString()}
                        </span>
                        <span className="text-base font-medium" style={{ color: 'var(--color-lamp-text)', opacity: 0.85 }}>
                            {' '}{t('lamp-bulletin.lampUnit')}
                        </span>
                    </div>
                )}
            </section>

            {/* Filter Section */}
            <div className="relative pb-6 px-4 md:px-8 max-w-sm mx-auto" style={{ zIndex: 20 }}>
                {/* 收合/展開 切換按鈕 - 固定右上角 */}
                <div className="flex justify-end mb-2">
                    <button
                        onClick={() => setFilterOpen(v => !v)}
                        className="flex h-7 w-7 items-center justify-center text-white opacity-70 hover:opacity-100 transition-opacity"
                    >
                        <span className="material-symbols-outlined text-xl leading-none">
                            {filterOpen ? 'keyboard_arrow_up' : 'search'}
                        </span>
                    </button>
                </div>

                {/* 展開狀態：篩選區 */}
                {filterOpen && (
                    <div className="flex flex-col gap-3">
                        {/* 只看自己的點燈 */}
                        <label className="flex items-center gap-2 cursor-pointer select-none self-end">
                            <input
                                type="checkbox"
                                checked={filterOnlySelf}
                                onChange={(e) => handleOnlySelfChange(e.target.checked)}
                                className="w-4 h-4 accent-[var(--color-lamp-red)] cursor-pointer"
                            />
                            <span className="text-sm font-medium" style={{ color: 'var(--color-lamp-text)' }}>
                                {t('lamp-bulletin.filterOnlyMine')}
                            </span>
                        </label>
                        {/* 日期搜尋 */}
                        <div className="flex items-center bg-white rounded-full shadow-md px-5 py-3 gap-3">
                            <input
                                type="text"
                                value={filterDate ? format(filterDate, 'yyyy/MM/dd') : ''}
                                readOnly
                                onClick={openDatePicker}
                                className="flex-1 bg-transparent text-base text-[var(--color-text-secondary)] focus:outline-none w-full cursor-pointer"
                                placeholder={t('lamp-bulletin.datePlaceholder')}
                            />
                            {filterDate && (
                                <button type="button" onClick={() => handleDateChange(null)} className="flex items-center justify-center text-[var(--color-text-secondary)] opacity-50 hover:opacity-100">
                                    <span className="material-symbols-outlined text-xl">close</span>
                                </button>
                            )}
                        </div>
                        {/* 姓名搜尋 */}
                        <div className="flex items-center bg-white rounded-full shadow-md px-5 py-3 gap-3 min-w-0">
                            <input
                                type="text"
                                value={filterDonor}
                                onChange={(e) => handleDonorChange(e.target.value)}
                                className="min-w-0 flex-1 bg-transparent text-base text-[var(--color-text-secondary)] focus:outline-none"
                                placeholder={t('lamp-bulletin.namePlaceholder')}
                            />
                            {filterDonor && (
                                <button
                                    type="button"
                                    onClick={() => handleDonorChange('')}
                                    className="flex h-6 w-6 shrink-0 items-center justify-center text-[var(--color-text-secondary)] opacity-50 hover:opacity-100"
                                >
                                    <span className="material-symbols-outlined text-xl leading-none">close</span>
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {showDatePicker && (
                <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40">
                    <div className="w-full max-w-sm rounded-t-3xl bg-white px-4 pb-6 pt-3">
                        <div className="mb-3 flex items-center justify-between">
                            <button
                                type="button"
                                onClick={() => setShowDatePicker(false)}
                                className="px-2 py-1 text-sm text-[var(--color-text-secondary)]"
                            >
                                {t('lamp-bulletin.cancel')}
                            </button>
                            <span className="text-sm font-semibold text-[var(--color-text-main)]">{t('lamp-bulletin.selectDate')}</span>
                            <button
                                type="button"
                                onClick={confirmDatePicker}
                                className="px-2 py-1 text-sm font-semibold text-[var(--color-lamp-red)]"
                            >
                                {t('lamp-bulletin.confirm')}
                            </button>
                        </div>

                        <Picker
                            value={pickerValue}
                            onChange={handlePickerChange}
                            height={180}
                            itemHeight={36}
                        >
                            <Picker.Column name="year">
                                {yearOptions.map((year) => (
                                    <Picker.Item key={year} value={year}>
                                        {({ selected }) => (
                                            <div className={`text-center ${selected ? 'font-semibold text-[var(--color-lamp-red)]' : 'text-[var(--color-text-secondary)]'}`}>
                                                {year}{t('lamp-bulletin.yearSuffix')}
                                            </div>
                                        )}
                                    </Picker.Item>
                                ))}
                            </Picker.Column>
                            <Picker.Column name="month">
                                {monthOptions.map((month) => (
                                    <Picker.Item key={month} value={month}>
                                        {({ selected }) => (
                                            <div className={`text-center ${selected ? 'font-semibold text-[var(--color-lamp-red)]' : 'text-[var(--color-text-secondary)]'}`}>
                                                {month}{t('lamp-bulletin.monthSuffix')}
                                            </div>
                                        )}
                                    </Picker.Item>
                                ))}
                            </Picker.Column>
                            <Picker.Column name="day">
                                {getDayOptions(pickerValue.year, pickerValue.month).map((day) => (
                                    <Picker.Item key={day} value={day}>
                                        {({ selected }) => (
                                            <div className={`text-center ${selected ? 'font-semibold text-[var(--color-lamp-red)]' : 'text-[var(--color-text-secondary)]'}`}>
                                                {day}{t('lamp-bulletin.daySuffix')}
                                            </div>
                                        )}
                                    </Picker.Item>
                                ))}
                            </Picker.Column>
                        </Picker>
                    </div>
                </div>
            )}

            {/* 燈籠動畫區 - overflow visible，燈可以飄出此區域 */}
            <section className="relative h-[95vh] overflow-visible">
                {backgroundLamps.map((lamp) => (
                    <div
                        key={lamp.uniqueId}
                        className="bg-lamp"
                        style={{
                            left: `${lamp.leftPosition}%`,
                            '--duration': `${lamp.duration}s`,
                        }}
                    >
                        <div
                            className="lamp-img"
                            style={{
                                width: `${lamp.scale * 120}px`,
                                opacity: lamp.opacity,
                            }}
                        >
                            <img src={lamp.image} alt={t('lamp-bulletin.bgLampAlt')} className="w-full h-auto" />
                        </div>
                    </div>
                ))}

                {foregroundLamps.map((lamp) => (
                    <div
                        key={lamp.uniqueId}
                        className="fg-lamp"
                        style={{
                            left: `${lamp.leftPosition}%`,
                            '--duration': `${lamp.duration}s`,
                            cursor: lamp.is_owner ? 'pointer' : 'default',
                        }}
                        {...(lamp.is_owner ? {
                            onClick: () => handleLampClick(lamp),
                            role: 'button',
                            tabIndex: 0,
                        } : {})}
                    >
                        <div className="flex flex-col items-center">
                            <div className="mb-3 text-center">
                                <p className="text-xs md:text-sm leading-relaxed" style={{ color: 'var(--color-lamp-text)' }}>
                                    {lamp.monastery_name}<br />
                                    {lamp.vower_name}<br />
                                    {t('lamp-bulletin.offering')}
                                </p>
                            </div>

                            <div className="lamp-img w-28 md:w-32 h-auto">
                                <img src={lamp.image} alt={t('lamp-bulletin.lampAlt')} className="w-full h-auto" />
                            </div>

                            <div className="w-0.5 h-5 bg-[var(--color-gold)] -mt-2"></div>

                            <div className="blessing-card">
                                <div
                                    className="bg-[var(--color-surface)] flex items-center justify-center w-8"
                                    style={{
                                        minHeight: '7.5em',
                                        boxShadow: '0 0 0.5em var(--color-gold)',
                                        writingMode: 'vertical-rl',
                                        textOrientation: 'mixed',
                                        backgroundImage: `url(${(lamp.uniqueId?.length ?? 0) % 2 === 0
                                            ? '/image/wishlamp/image_250_h120.png'
                                            : '/image/wishlamp/image_252_h120.png'
                                            })`,
                                        backgroundSize: '100% 100%',
                                        backgroundRepeat: 'no-repeat',
                                    }}
                                >
                                    <span className="text-[var(--color-text-main)] font-light text-lg leading-7">
                                        {TITLE_ZH_MAP[lamp.title] || lamp.title}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </section>

            {/* 浮動「我要點燈」按鈕 */}
            <button
                onClick={() => navigate(ROUTES.LAMP_LIGHTING, { state: { from: ROUTES.LAMP_BULLETIN } })}
                className="fixed top-45 right-3 z-30 w-16 h-16 md:w-20 md:h-20 text-white rounded-full hover:shadow-xl transition-all hover:scale-105 flex flex-col items-center justify-center"
                style={{
                    backgroundColor: '#BC313D',
                    boxShadow: '0 4px 12px rgba(188,49,61,0.4)',
                }}
            >
                <span className="text-xs md:text-sm font-medium leading-tight">{t('lamp-bulletin.lightLampCta')}</span>
            </button>
        </div>
    );
};

LampBulletinPage.propTypes = {
    pageTitle: PropTypes.shape({
        title: PropTypes.string,
        subtitle: PropTypes.string,
    }),
    quote: PropTypes.shape({
        text: PropTypes.string,
        dedication: PropTypes.string,
    }),
};

export default LampBulletinPage;
